/**
 * React binding for the engine.
 *
 * The engine is deliberately not reactive — it is a plain deterministic object.
 * This hook owns the one bridge: run a mutation, then bump a version counter so
 * React re-reads. Keeping the engine free of React is what lets the same code run
 * in tests, in Node, and later on a server for the Living World.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  GameSession,
  ManipulationError,
  type ClassId,
  type CombatTactic,
  type DiceFace,
  type ManipulationKind,
  type SessionEvent,
} from '@lastroll/engine';
import { createResolver, getContentBundle, getContentDb } from '@lastroll/content';
import { AudioEngine, WARMUP_IDS } from '@lastroll/audio';
import { fxFor, type FxCue } from '../components/fx.js';

const SAVE_KEY = 'lastroll.save.v1';
const MAX_FEED = 40;

export interface UseGame {
  session: GameSession | null;
  /** Increments on every mutation; components read engine state after this changes. */
  version: number;
  feed: SessionEvent[];
  error: string | null;
  rolling: boolean;
  /** The effect to play for the last batch of events, or null. */
  fx: FxCue | null;
  audio: AudioEngine;
  start: (classId: ClassId, seed: string) => void;
  roll: (context?: string) => void;
  manipulate: (kind: ManipulationKind) => void;
  moveTo: (roomId: string) => void;
  /** 쉴 곳에서 쉰다 (GDD §12-1). */
  rest: () => void;
  stay: () => void;
  choose: (choiceId: string, face?: DiceFace | null) => void;
  hesitate: () => void;
  fight: (tactic: CombatTactic) => void;
  playCard: (cardId: string) => void;
  save: () => void;
  load: () => boolean;
  hasSave: boolean;
  reset: () => void;
  dismissError: () => void;
}

export function useGame(): UseGame {
  /*
   * 세션은 **ref 에 둔다.** React state 가 아니다.
   *
   * 엔진은 의도적으로 불변이 아닌, 직접 변이되는 객체다. 그런 걸 `setState`
   * 업데이터 안에서 건드리면 안 된다 — StrictMode 는 개발 중 업데이터를 두 번
   * 호출하기 때문이다. 실제로 그래서 `manipulate` 한 번에 운명 빚이 두 번 붙었고,
   * 다른 행동은 두 번째 호출에서 예외를 던져 엉뚱한 오류 토스트를 띄웠으며,
   * 피드에 같은 줄이 두 번 쌓였다.
   *
   * 업데이터는 순수해야 한다. 그래서 변이는 ref 위에서 하고, 화면 갱신은
   * `version` 카운터로만 알린다.
   */
  const sessionRef = useRef<GameSession | null>(null);
  const [session, setSession] = useState<GameSession | null>(null);
  const [version, setVersion] = useState(0);
  const [feed, setFeed] = useState<SessionEvent[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [rolling, setRolling] = useState(false);
  const [fx, setFx] = useState<FxCue | null>(null);
  const [hasSave, setHasSave] = useState(false);
  const fxSeq = useRef(0);

  const audio = useMemo(() => new AudioEngine(), []);
  const rollTimer = useRef<number | null>(null);

  const bundle = useMemo(() => getContentBundle(), []);
  const resolver = useMemo(() => createResolver(getContentDb()), []);

  useEffect(() => {
    try {
      setHasSave(localStorage.getItem(SAVE_KEY) !== null);
    } catch {
      // Private browsing or blocked storage: saving simply is not available.
      setHasSave(false);
    }
  }, []);

  useEffect(() => () => audio.dispose(), [audio]);

  /** Drain presentation events, play their sounds, and push them to the feed. */
  const drain = useCallback(
    (s: GameSession) => {
      const events = s.drain();
      for (const ev of events) {
        if (ev.sfxId) {
          const material = s.currentDie.material;
          const cursed = s.state.dice.fateDebt >= 25;
          audio.play(ev.sfxId, {
            material,
            cursed,
            ...(ev.kind === 'rolled' && typeof ev.data?.['record'] === 'object'
              ? { face: (ev.data['record'] as { finalFace?: number }).finalFace }
              : {}),
          });
        }
      }
      // The same events that pick a sound pick an effect. Sight and sound come
      // from one source, so they can never disagree about what just happened.
      const kind = fxFor(events);
      if (kind) {
        fxSeq.current += 1;
        setFx({ kind, id: fxSeq.current });
      }

      const room = s.currentRoom;
      if (room?.musicId) audio.playMusic(room.musicId);
      if (events.length > 0) {
        // `encounterOpened` carries the body text that EncounterPanel already
        // renders in full; echoing it in the feed was pure duplication.
        const feedable = events.filter((e) => e.text && e.kind !== 'encounterOpened');
        if (feedable.length > 0) setFeed((prev) => [...prev, ...feedable].slice(-MAX_FEED));
      }
      setVersion((v) => v + 1);
    },
    [audio],
  );

  /** Wrap a mutation so engine errors surface as UI state, never as a crash. */
  const act = useCallback(
    (fn: (s: GameSession) => void) => {
      const current = sessionRef.current;
      if (!current) return;
      try {
        fn(current);
        setError(null);
      } catch (e) {
        const msg =
          e instanceof ManipulationError
            ? e.message
            : e instanceof Error
              ? e.message
              : String(e);
        setError(msg);
      }
      drain(current);
    },
    [drain],
  );

  const start = useCallback(
    (classId: ClassId, seed: string) => {
      void audio.start().then(() => audio.warmUp(WARMUP_IDS));
      const s = new GameSession({ runSeed: seed, classId, content: bundle });
      setFeed([]);
      setError(null);
      sessionRef.current = s;
      setSession(s);
      drain(s);
    },
    [audio, bundle, drain],
  );

  const roll = useCallback(
    (context = 'movement') => {
      // The engine resolves immediately; the 600 ms is the tumble animation only,
      // matched to the dice SFX length so sight and sound land together.
      setRolling(true);
      if (rollTimer.current !== null) window.clearTimeout(rollTimer.current);
      rollTimer.current = window.setTimeout(() => setRolling(false), 600);
      act((s) => s.roll(context));
    },
    [act],
  );

  const manipulate = useCallback(
    (kind: ManipulationKind) => {
      setRolling(true);
      if (rollTimer.current !== null) window.clearTimeout(rollTimer.current);
      rollTimer.current = window.setTimeout(() => setRolling(false), 600);
      act((s) => void s.manipulate(kind));
    },
    [act],
  );

  const moveTo = useCallback((roomId: string) => act((s) => s.moveTo(roomId)), [act]);
  const rest = useCallback(() => act((s) => void s.rest()), [act]);
  const stay = useCallback(() => act((s) => s.stay()), [act]);
  const choose = useCallback(
    (choiceId: string, face: DiceFace | null = null) => act((s) => s.choose(choiceId, face)),
    [act],
  );
  const hesitate = useCallback(() => act((s) => void s.hesitate()), [act]);
  const fight = useCallback((tactic: CombatTactic) => act((s) => s.fight(tactic)), [act]);
  const playCard = useCallback((cardId: string) => act((s) => s.playCard(cardId)), [act]);

  const save = useCallback(() => {
    const current = sessionRef.current;
    if (!current) return;
    try {
      localStorage.setItem(SAVE_KEY, JSON.stringify(current.save()));
      setHasSave(true);
      setError(null);
    } catch {
      setError('저장에 실패했습니다. 브라우저 저장소를 사용할 수 없습니다.');
    }
  }, []);

  const load = useCallback((): boolean => {
    try {
      const raw = localStorage.getItem(SAVE_KEY);
      if (!raw) return false;
      void audio.start().then(() => audio.warmUp(WARMUP_IDS));
      const { session: loaded, unresolved } = GameSession.load(JSON.parse(raw), bundle, {
        resolver,
      });
      sessionRef.current = loaded;
      setSession(loaded);
      setFeed([]);
      setError(
        unresolved.length > 0
          ? `이 빌드에 없는 콘텐츠 ${unresolved.length}건을 제외하고 불러왔습니다.`
          : null,
      );
      setVersion((v) => v + 1);
      return true;
    } catch (e) {
      setError(`불러오기 실패: ${e instanceof Error ? e.message : String(e)}`);
      return false;
    }
  }, [audio, bundle, resolver]);

  const reset = useCallback(() => {
    audio.stopMusic(0.4);
    sessionRef.current = null;
    setSession(null);
    setFeed([]);
    setError(null);
    setFx(null);
  }, [audio]);

  return {
    session,
    version,
    feed,
    error,
    rolling,
    fx,
    audio,
    start,
    roll,
    manipulate,
    moveTo,
    rest,
    stay,
    choose,
    hesitate,
    fight,
    playCard,
    save,
    load,
    hasSave,
    reset,
    dismissError: () => setError(null),
  };
}
